import { IOrganization } from "@/api";
import { getPublicPresignedUrl, uploadToStorage } from "@/api/media/media";
import { uploadOrganizationLogo } from "@/api/organizations/organizations";
import { PageTitle } from "@/components/shared/layout/PageTitle";
import { Upload } from "@mui/icons-material";
import { alpha, Box, Stack, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { FileRejection, useDropzone } from "react-dropzone";

const DEFAULT_ACCEPT = {
    "image/png": [".png"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/svg+xml": [".svg"],
};

const DEFAULT_MAX_SIZE = 200 * 1024 * 1024; // 200 MB

interface IProps {
    /**
     * Accepted file types and extensions
     * @default DEFAULT_ACCEPT
     */
    accept?: Record<string, string[]>;
    maxSize?: number;
    organization: IOrganization;
}

export const LogoFileDrop = (props: IProps) => {
    const {
        accept = DEFAULT_ACCEPT,
        maxSize = DEFAULT_MAX_SIZE,
        organization,
    } = props;

    const queryClient = useQueryClient();
    const [isDragging, setIsDragging] = useState(false);
    const [uploadingFile, setUploadingFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    // Create preview URL when a file is selected for upload
    useEffect(() => {
        if (uploadingFile) {
            const url = URL.createObjectURL(uploadingFile);
            setPreviewUrl(url);
            return () => {
                URL.revokeObjectURL(url);
            };
        } else {
            setPreviewUrl(null);
        }
    }, [uploadingFile]);

    const uploadMutation = useMutation({
        mutationFn: async (file: File) => {
            // Step 1: Get presigned URL with uploads base_path
            const presignedUrl = await getPublicPresignedUrl({
                filename: file.name,
                content_type: file.type,
            });

            // Step 2: Upload file to S3
            await uploadToStorage(file, presignedUrl);

            // Step 3: Update organization with logo URL
            const logoUrl = `public/${presignedUrl.object_name}`;
            return await uploadOrganizationLogo(organization.id, {
                logo_url: logoUrl,
            });
        },
        onSuccess: () => {
            enqueueSnackbar("Logo uploaded successfully!", {
                variant: "success",
                persist: false,
            });
            // Invalidate the query to refetch and show the new logo
            queryClient.invalidateQueries({
                queryKey: ["organizationLogo", organization.id],
            });
        },
        onError: (error: unknown) => {
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : "An unknown error occurred";
            enqueueSnackbar(`Logo upload failed: ${errorMessage}`, {
                variant: "error",
                persist: false,
            });
            // Clear the uploading state on error
            setUploadingFile(null);
        },
    });

    const onDrop = useCallback(
        (acceptedFiles: File[], fileRejections: FileRejection[]) => {
            setIsDragging(false);

            if (fileRejections.length > 0) {
                enqueueSnackbar(
                    `File upload failed: ${fileRejections[0].errors[0].message}`,
                    {
                        variant: "error",
                        persist: false,
                    }
                );
                return;
            }

            if (acceptedFiles.length > 0) {
                const file = acceptedFiles[0];
                setUploadingFile(file);
                uploadMutation.mutate(file);
            }
        },
        [uploadMutation]
    );

    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const onDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const { getRootProps, getInputProps } = useDropzone({
        maxFiles: 1,
        accept,
        maxSize,
        onDrop,
        onDragOver,
        onDragLeave,
    });

    // If we're uploading and have a preview, show the image with loading state
    if (previewUrl) {
        return (
            <Stack direction={"row"} gap={2} alignItems={"center"}>
                <Box
                    sx={{
                        position: "relative",
                        width: 80,
                        height: 80,
                        p: 0.5,
                        border: 1,
                        borderColor: (theme) => theme.palette.gray.light,
                        borderRadius: 2,
                    }}
                >
                    <Box
                        component="img"
                        src={previewUrl}
                        sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            opacity: 0.6,
                        }}
                    />
                </Box>
                <PageTitle title={organization.name} />
            </Stack>
        );
    }

    // Default upload component
    return (
        <Stack direction={"row"} alignItems={"center"} gap={2}>
            <Box
                sx={{
                    width: 80,
                    height: 80,
                    border: 2,
                    borderStyle: "dashed",
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    bgcolor: (theme) =>
                        isDragging
                            ? alpha(theme.palette.secondary.main, 0.1)
                            : undefined,
                    borderColor: (theme) =>
                        isDragging
                            ? theme.palette.secondary.main
                            : theme.palette.gray.light,
                    color: (theme) => theme.palette.gray.main,
                    "&:hover": {
                        borderColor: (theme) => theme.palette.gray.main,
                    },
                }}
                {...getRootProps()}
            >
                <input {...getInputProps()} />

                <Upload fontSize="large" />
            </Box>
            <Stack>
                <PageTitle title={organization.name} />
                <Typography color="text.secondary">
                    Click to upload logo
                </Typography>
            </Stack>
        </Stack>
    );
};
